import { apiClient } from '@/api/api-client';
import { ApiResponse } from '@/types/api-response';
import { normalizeFbMessage } from '@/services/chat-message-normalizer';
import {
  FacebookComment,
  FbConversation,
  FBLive,
  FbLoginToken,
  FBMessage,
  FbPage,
  FBPost,
  FetchCommentRespoonse,
  OrderByComment,
  PageResult,
} from '@/types/fb-type';
export async function createFbLoginToken(): Promise<ApiResponse<FbLoginToken>> {
  const response = await apiClient.get<FbLoginToken, {}>('/fbapi/start', {});
  return response;
}

export async function fetchFbPages(code?: string, state?: string): Promise<ApiResponse<[FbPage]>> {
  const response = await apiClient.get<[FbPage], {}>('/fbapi/callback', { code, state });
  //console.log('fb page list ', response);
  return response;
}
export async function connectFbPage(pageId: string): Promise<ApiResponse<[FBPost]>> {
  const response = await apiClient.post<[FBPost], {}>('/fbapi/connect', { pageId });
  return response;
}

export async function getConversationsForPage(
  after?: string,
): Promise<ApiResponse<PageResult<FbConversation>>> {
  const url = after ? `/fb/conversations?after=${encodeURIComponent(after)}` : '/fb/conversations';
  const response = await apiClient.get<PageResult<FbConversation> | FbConversation[], {}>(url, {});

  const raw = response.data;
  const toConversation = (item: FbConversation) => Object.assign(new FbConversation(), item);

  const pageResult: PageResult<FbConversation> = Array.isArray(raw)
    ? {
        data: raw.map(toConversation),
        nextCursor: null,
        prevCursor: null,
      }
    : {
        data: (raw?.data ?? []).map(toConversation),
        nextCursor: raw?.nextCursor ?? null,
        prevCursor: raw?.prevCursor ?? null,
      };

  return { ...response, data: pageResult };
}

export async function getMessageForConversation(
  conversationId: string,
  cursor?: string,
): Promise<ApiResponse<PageResult<FBMessage>>> {
  const url = cursor
    ? `/fb/conversations/${conversationId}/messages?after=${encodeURIComponent(cursor)}`
    : `/fb/conversations/${conversationId}/messages`;
  const response = await apiClient.get<PageResult<FBMessage>, {}>(url, {});

  const messages = response.data?.data?.map(
    (item) => normalizeFbMessage(item) || Object.assign(new FBMessage(), item),
  );
  return {
    ...response,
    data: {
      data: messages || [],
      nextCursor: response.data?.nextCursor || null,
      prevCursor: response.data?.prevCursor || null,
    },
  };
}

export async function sendChatMessage(
  conversationId: string,
  content: string,
): Promise<ApiResponse<FBMessage>> {
  const response = await apiClient.post<FBMessage, {}>(
    `/fb/conversations/${conversationId}/messages`,
    { message: content },
  );
  if (response.data) {
    response.data = normalizeFbMessage(response.data) || Object.assign(new FBMessage(), response.data);
  }

  return response;
}

export async function sendChatVoice(
  conversationId: string,
  formData: FormData,
): Promise<ApiResponse<FBMessage>> {
  const response = await apiClient.postForm<FBMessage>(
    `/fb/conversations/${conversationId}/voice`,
    formData,
  );
  if (response.data) {
    response.data = normalizeFbMessage(response.data) || Object.assign(new FBMessage(), response.data);
  }
  return response;
}

export async function getCurrentPage() {
  const response = await apiClient.get<FbPage, {}>(`/fb/page`);
  return response;
}
export async function refeshConfig() {
  const response = await apiClient.get<string, {}>(`/refresh-config`);
  return response;
}

export async function getLives() {
  const response = await apiClient.get<FBLive[], {}>(`/fb/live`);
  return response;
}

export async function getPosts() {
  const response = await apiClient.get<FBPost[], {}>(`/fb/posts`, {});
  return response;
}
export async function getComments(postId: string) {
  const response = await apiClient.get<FacebookComment[], {}>(`/fb/posts/${postId}/comments`, {});
  return response;
}
export async function fetchLiveComments(postId: string, since: string | null) {
  let url = `/fb/live/${postId}/comments`;
  if (since) {
    url = `/fb/live/${postId}/comments?since=${since}`;
  } else {
  }
  const response = await apiClient.get<FetchCommentRespoonse, {}>(url, {});
  return response;
}
export async function getOrderByComments(postId: string) {
  const response = await apiClient.get<OrderByComment[], {}>(
    `/fb/order_by_comments/${postId}/by_post`,
    {},
  );
  return response;
}

export async function getConversationById(id: string): Promise<ApiResponse<FbConversation>> {
  const response = await apiClient.get<FbConversation, {}>(`/fb/conversations/${id}`, {});
  response.data = Object.assign(new FbConversation(), response.data);
  return response;
}
export async function getOrderByCommentForComment(commentId: string) {
  const response = await apiClient.get<OrderByComment, {}>(
    `/fb/order_by_comments/${commentId}/by_comment`,
    {},
  );
  return response;
}

export async function whiteListDomain() {
  debugger;
  const response = await apiClient.post<string, {}>(`/fb/page/whitelist-domain`, {});
  return response;
}

/**
 * Send the signed_request from Messenger Extensions getContext() to the backend.
 * The backend decodes it (using App Secret) to get a verified PSID and can then
 * send messages within the 24-hour messaging window.
 * POST /fb/page/verify-context
 */
export async function verifyMessengerContext(signedRequest: string, orderId: string) {
  const response = await apiClient.post<{ psid: string }, {}>(`/fb/page/verify-context`, {
    signedRequest,
    orderId,
  });
  return response;
}

export async function fbProductCodes() {
  const response = await apiClient.get<string[], {}>(`/fb/detect-comment/product-codes`, {});
  return response;
}
export async function fbVariantCodes() {
  const response = await apiClient.get<string[], {}>(`/fb/detect-comment/variant-codes`, {});
  return response;
}

/**
 * Fetch the last live comment left by a FB user (by their PSID / userId).
 * Used as a fallback when the Messenger send API is unavailable
 * (e.g. the user only commented on a Live, never opened a DM thread).
 * Returns the comment ID if found, or null if not available.
 */
export async function getLastLiveCommentByUser(
  userId: string,
): Promise<ApiResponse<{ commentId: string; message: string; fromName: string } | null>> {
  const response = await apiClient.get<
    { commentId: string; message: string; fromName: string } | null,
    {}
  >(`/fb/live/last-comment?userId=${encodeURIComponent(userId)}`, {});
  return response;
}
